/**
 * Setting a paper — the rules the editor, the API and the exam all read.
 *
 * Browser-safe on purpose (it imports only types): the paper editor shows the
 * same "about N min" and "answer in ≤ N words" that publish stamps on the
 * Question rows, and the exam embeds the same videos the editor said it could.
 * Each rule had a copy in the prototype with a comment saying "if one changes,
 * change both" — which is the copy that drifts. One copy, here.
 */

import type { CustomPaperItem, QuestionType } from './domain';

/** A paper never carries an audio question; the editor's four types. */
export type PaperQuestionType = Exclude<QuestionType, 'AUDIO'>;

/**
 * The question types a paper may carry: options and written answers.
 *
 * Restricted at the WRITERS of `Paper.questionIds` (seed, publish, practice,
 * drills, diagnostic) and at the authoring forms, not at each reader: a paper
 * that holds supported types can use the same exam, reports and cards.
 */
export const ENABLED_QUESTION_TYPES: readonly PaperQuestionType[] = [
  'MCQ',
  'SHORT_DESC',
  'MEDIUM_DESC',
  'LONG_DESC',
];

export const isEnabledQuestionType = (type: QuestionType | null | undefined): boolean =>
  !!type && (ENABLED_QUESTION_TYPES as readonly string[]).includes(type);

// ─────────────────────────── TEAM ───────────────────────────

/**
 * The four parts of a question's support, in the client's order: a Hint (one
 * per question), Explanation (a line of theory, then the worked steps), the
 * Answer with its marking scheme, and common Mistakes.
 */
export const TEAM_PARTS = ['T', 'E', 'A', 'M'] as const;
export type TeamPart = (typeof TEAM_PARTS)[number];

export const TEAM_PART_LABEL: Record<TeamPart, string> = {
  T: 'Hint',
  E: 'Explanation',
  A: 'Answer',
  M: 'Common mistakes',
};

/** One step of a marking scheme. Steps must total the question's marks. */
export interface MarkingStepDTO {
  step: string;
  marks: number;
}

export const schemeTotal = (scheme: readonly MarkingStepDTO[] | null | undefined): number =>
  (scheme ?? []).reduce((sum, s) => sum + (Number(s.marks) || 0), 0);

const blank = (s: string | null | undefined): boolean => !s?.trim();

/** Which TEAM parts of an item still have nothing in them. */
export function blankTeamParts(item: {
  lightHint?: string | null;
  strongHint?: string | null;
  explanation?: string | null;
  answer?: string | null;
  markingScheme?: readonly MarkingStepDTO[] | null;
  mistakes?: readonly string[] | null;
}): TeamPart[] {
  return TEAM_PARTS.filter((part) => {
    switch (part) {
      case 'T':
        // One hint per question, kept in `lightHint`; `strongHint` is retired.
        return blank(item.lightHint);
      case 'E':
        return blank(item.explanation);
      case 'A':
        return blank(item.answer) || !item.markingScheme?.length;
      case 'M':
        return !item.mistakes?.some((m) => m.trim());
    }
  });
}

// ─────────────────────────── time and length ───────────────────────────

/**
 * How long a written answer should run, in words, by type — used when the
 * teacher leaves the limit alone. The CBSE pattern (two or three sentences, a
 * paragraph, a page side). These are CWY's defaults, not a board's rule; the
 * teacher can set any question's limit. An MCQ has no written answer to limit.
 */
export const WORD_LIMIT: Record<PaperQuestionType, number | null> = {
  MCQ: null,
  SHORT_DESC: 40,
  MEDIUM_DESC: 80,
  LONG_DESC: 150,
};

/** The limit a question carries: the teacher's, else the type's default. */
export function wordLimitFor(item: {
  type?: QuestionType | null;
  wordLimit?: number | null;
}): number | null {
  const type = item.type ?? 'SHORT_DESC';
  if (type === 'MCQ' || type === 'AUDIO') return null;
  return item.wordLimit ?? WORD_LIMIT[type];
}

/**
 * The time a question gets when the teacher leaves it alone: a minute for an
 * MCQ, 90 s a mark for a written answer, between one and ten minutes.
 *
 * This is the publish rule (`questionFieldsFromItem` in `@cwy/core` reads
 * here), so leaving the editor's Time field alone changes nothing.
 */
export function defaultAllocatedSeconds(type: QuestionType, marks: number): number {
  if (type === 'MCQ') return 60;
  return Math.min(600, Math.max(60, Math.round(90 * marks)));
}

/** A question's own time: the teacher's, else the default for its type and marks. */
export function questionSeconds(item: {
  type?: QuestionType | null;
  marks?: number | null;
  allocatedSeconds?: number | null;
}): number {
  return (
    item.allocatedSeconds ?? defaultAllocatedSeconds(item.type ?? 'SHORT_DESC', item.marks ?? 1)
  );
}

/**
 * A whole paper's time: the sum of its questions' own times. Read by the
 * editor's "about N min", the printed paper's "time allowed" and publish's
 * `Paper.totalSeconds`, so all three are the same figure.
 *
 * There are exactly two clocks in an exam — this one and each question's own —
 * and nothing else. A separate reading window for written answers used to sit
 * in front of the question clock; it was removed, not hidden.
 */
export function paperSeconds(
  items: readonly {
    type?: QuestionType | null;
    marks?: number | null;
    allocatedSeconds?: number | null;
  }[],
): number {
  return items.reduce((sum, i) => sum + questionSeconds(i), 0);
}

/**
 * Words in an answer, as a limit counts them. A formula in `$…$` is one word
 * however long its LaTeX is — a student writes `$x^2 + 2x + 1$` as one
 * expression, not seven tokens.
 */
export function wordCount(text: string): number {
  const flat = text.replace(/\$\$[\s\S]+?\$\$|\$[^$]+?\$/g, ' F ');
  return flat.split(/\s+/).filter(Boolean).length;
}

// ─────────────────────────── video ───────────────────────────

/**
 * The id of the ONE video a link names — a watch, share or embed link, or a
 * bare id — and null for anything else, a search included.
 */
export function youTubeVideoId(ref: string | null | undefined): string | null {
  if (!ref) return null;
  const match = /(?:v=|youtu\.be\/|embed\/)([\w-]{11})/.exec(ref);
  return match ? match[1]! : /^[\w-]{11}$/.test(ref) ? ref : null;
}

/**
 * The embed URL for a link that names one video, and null for anything else.
 * The exam plays the first kind in place; a search goes to the video library.
 */
export function youTubeEmbedUrl(ref: string | null | undefined): string | null {
  const id = youTubeVideoId(ref);
  return id ? `https://www.youtube.com/embed/${id}` : null;
}

/**
 * What a video link SEARCHES for — the `search_query` of a YouTube results
 * link, as `videoSearchFor` writes them — or null for anything else. A search
 * page cannot be framed, so the student's video library runs this query
 * through `GET /videos/search` and plays the videos it finds in place.
 */
export function youTubeSearchQuery(ref: string | null | undefined): string | null {
  if (!ref) return null;
  try {
    const url = new URL(ref);
    if (!/(^|\.)youtube\.com$/.test(url.hostname) || url.pathname !== '/results') return null;
    const query = url.searchParams.get('search_query')?.trim();
    return query ? query : null;
  } catch {
    return null;
  }
}

/**
 * The third-attempt video when nobody chose one: a search for the sub-topic.
 *
 * A question must not reach a student's third attempt with no video at all,
 * and a search is honest about not being a vetted pick.
 */
export function videoSearchFor(...path: Array<string | null | undefined>): string {
  const terms = path.filter((p): p is string => Boolean(p?.trim())).join(' ');
  return `https://www.youtube.com/results?search_query=${encodeURIComponent(terms)}`;
}

// ─────────────────────────── the paper code ───────────────────────────

/**
 * A paper's printed code: six digits, a pure function of its id.
 *
 * Printed in boxes on the question paper and the OMR sheet, and read back by
 * whoever files or scans them, so it must be stable across reloads, devices and
 * deploys — FNV-1a over the id, nothing random. Pass the PUBLISHED paper's id
 * once there is one, so the class report and the sheets carry the same code.
 */
export function paperCodeFor(paperId: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < paperId.length; i += 1) {
    h ^= paperId.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return String((h >>> 0) % 1_000_000).padStart(6, '0');
}

// ─────────────────────────── concept tags and the bank ───────────────────────────

/** A sub-topic a question in this subject can be tagged with. */
export interface ConceptOption {
  subtopicId: string;
  name: string;
  chapterId: string;
  /** Shown as "Topic" — a Chapter in the schema. */
  chapterName: string;
}

/** A tag, and whether anything in the question actually named it. */
export type ConceptTagResult = (ConceptOption & { sure: boolean }) | null;

/**
 * A bank question as the paper editor's picker shows it. Teacher-only: it
 * carries the answer key and scheme, which is what lets it arrive in a paper
 * with its TEAM already written.
 */
export interface BankQuestionDTO {
  id: string;
  type: PaperQuestionType;
  text: string;
  maxMarks: number;
  allocatedSeconds: number;
  subtopicId: string;
  subtopicName: string;
  chapterId: string;
  chapterName: string;
  options: string[] | null;
  correctOptionIdx: number | null;
  correctAnswer: string;
  tipsText: string;
  strongHintText: string;
  explanation: string;
  markingScheme: MarkingStepDTO[];
  mistakes: string[];
  wordLimit: number | null;
  /** Only a link someone chose; a generated search is not carried into a paper. */
  videoRef: string | null;
  /** Written in this workspace, as opposed to CWY's shared bank. */
  authored: boolean;
}

export interface QuestionBankPage {
  total: number;
  results: BankQuestionDTO[];
  chapters: { id: string; name: string }[];
}

/** The page size of `GET /teacher/question-bank`. */
export const BANK_PAGE = 30;

// ─────────────────────────── from your content ───────────────────────────

/** What to make of a teacher's material. "Only ticked" is the review step, not a mode. */
export type ContentIngestMode = 'FROM_CONTENT' | 'SAME' | 'SIMILAR';

/** One question read out of the material, for the teacher to tick or not. */
export interface ContentCandidate {
  key: string;
  /** Where in the material it came from, so the teacher can check it. */
  source: string;
  item: CustomPaperItem;
  /** False when nothing in the question named a sub-topic and the tag is a guess. */
  tagSure: boolean;
}

export interface ContentIngestResult {
  candidates: ContentCandidate[];
  /** Said on the page — e.g. that AI was unavailable and a plain reading stood in. */
  note: string | null;
}
